import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import List from '@mui/material/List';
import ListItemText, { listItemTextClasses } from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { normalizeClassnames, spacingToNumber } from '../../../internals/utils';
import SovosCollapse from '../../../sovos-collapse';
import SovosFixedWidthText from '../../../sovos-fixed-width-text';
import SectionHeader from './SectionHeader';
import HEADER from '../../constants/header';
import { hasNestedLinks, isLinkSelected } from '../../../internals/navigation/linkUtils';
import { navigationLinksPropTypes } from '../../../internals/prop-types/navigationLinks';
import BaseLink from './BaseLink';
import BaseIcon from './BaseIcon';
import BaseSelectedIndicator from './BaseSelectedInidicator';
import BaseToggle from './BaseToggle';
const {
  primary,
  inset
} = listItemTextClasses;
const getIndicatorStyles = spacing => ({
  margin: spacingToNumber(spacing(0.625)),
  // 5px
  width: spacingToNumber(spacing(0.5))
});
const StyledLink = styled(BaseLink)(_ref => {
  let {
    theme: {
      spacing
    }
  } = _ref;
  return {
    margin: "".concat(spacing(0.25), " ").concat(spacing(), " 0 ").concat(spacing()),
    paddingLeft: spacing(0.375),
    // 3px
    paddingRight: spacing()
  };
});
const StyledIcon = styled(BaseIcon)(_ref2 => {
  let {
    theme: {
      spacing
    }
  } = _ref2;
  return {
    marginRight: spacing()
  };
});
const StyledSelectedIndicator = styled(BaseSelectedIndicator)(_ref3 => {
  let {
    theme: {
      spacing
    }
  } = _ref3;
  const indicatorStyles = getIndicatorStyles(spacing);
  return {
    '&.sovosNavigationLinks__linkSelectedIndicator': {
      marginRight: indicatorStyles.margin,
      width: indicatorStyles.width
    }
  };
});
const StyledToggle = styled(BaseToggle)(_ref4 => {
  let {
    theme: {
      transitions
    }
  } = _ref4;
  return {
    transition: "transform ".concat(transitions.duration.short, "ms"),
    '&.sovosNavigationLinks__nestedLinksToggle--expanded': {
      transform: 'rotate(90deg)'
    }
  };
});
const LinkText = styled(ListItemText)(_ref5 => {
  let {
    theme: {
      spacing,
      typography
    }
  } = _ref5;
  const indicatorStyles = getIndicatorStyles(spacing);
  return {
    ["&:not(.".concat(inset, ") .").concat(primary)]: {
      ...typography.body1,
      flex: '1 0 auto',
      marginLeft: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    ["&.".concat(inset)]: {
      paddingLeft: spacingToNumber(spacing(6)) - indicatorStyles.width - indicatorStyles.margin,
      ["& .".concat(primary)]: {
        ...typography.body2
      }
    }
  };
});
function getLinkIdentifier(link) {
  var _link$id;
  return (_link$id = link.id) !== null && _link$id !== void 0 ? _link$id : link.label;
}
function expandSelectedLink(links, selectedLink) {
  const parentLink = links.find(element => {
    if (element.nestedLinks && element.nestedLinks.length > 0) {
      return element.nestedLinks.find(e => isLinkSelected(e, selectedLink)) !== undefined;
    }
    return false;
  });
  if (parentLink) {
    return {
      [getLinkIdentifier(parentLink)]: true
    };
  }
  return {};
}
const NavigationLinks = _ref6 => {
  let {
    selectedLink,
    onLinkClick,
    links
  } = _ref6;
  const [expandedLinks, setExpandedLinks] = useState(expandSelectedLink(links, selectedLink));
  const handleLinkClick = link => {
    const expand = hasNestedLinks(link);
    if (expand) {
      setExpandedLinks(prevState => {
        const isOpen = prevState[getLinkIdentifier(link)] || false;
        return {
          ...prevState,
          [getLinkIdentifier(link)]: !isOpen
        };
      });
    } else {
      onLinkClick(link);
    }
  };
  const renderNestedLinkArrow = (link, isNested) => {
    if (!hasNestedLinks(link) || isNested) return null;
    const isExpanded = !!expandedLinks[getLinkIdentifier(link)];
    return /*#__PURE__*/React.createElement(StyledToggle, {
      isExpanded: isExpanded
    });
  };
  const renderIcon = (link, isNested) => {
    if (isNested || !link.icon) {
      return null;
    }
    return /*#__PURE__*/React.createElement(StyledIcon, {
      element: link.icon
    });
  };
  const renderNestedList = (link, isNested) => {
    if (!isNested && hasNestedLinks(link)) {
      return /*#__PURE__*/React.createElement(SovosCollapse, {
        in: expandedLinks[getLinkIdentifier(link)],
        timeout: "auto",
        unmountOnExit: true
      }, /*#__PURE__*/React.createElement(List, {
        disablePadding: true
      }, link.nestedLinks.map(nestedLink =>
      // eslint-disable-next-line no-use-before-define
      renderLinkItem(nestedLink, true))));
    }
    return null;
  };
  const shouldShowSelectedIndicator = link => {
    if (isLinkSelected(link, selectedLink)) {
      return true;
    }
    if (hasNestedLinks(link) && !expandedLinks[getLinkIdentifier(link)]) {
      return !!link.nestedLinks.find(nl => isLinkSelected(nl, selectedLink));
    }
    return false;
  };
  const renderLinkItem = (link, isNested) => {
    const normalizedLinkText = normalizeClassnames(link.label);
    const isSelected = shouldShowSelectedIndicator(link);
    if (link.type === HEADER) {
      return /*#__PURE__*/React.createElement(SectionHeader, {
        key: link.label,
        label: link.label
      });
    }
    return /*#__PURE__*/React.createElement(Fragment, {
      key: link.label
    }, /*#__PURE__*/React.createElement(StyledLink, {
      isDisabled: link.disabled,
      isNested: isNested,
      isSelected: isSelected,
      onClick: link.disabled ? null : () => handleLinkClick(link)
    }, /*#__PURE__*/React.createElement(StyledSelectedIndicator, {
      element: "div",
      isSelected: isSelected
    }), renderIcon(link, isNested), /*#__PURE__*/React.createElement(LinkText, {
      inset: isNested,
      className: clsx('sovosNavigationLinks__linkText', "sovosNavigationLinks__linkText-".concat(normalizedLinkText)),
      primary: /*#__PURE__*/React.createElement(SovosFixedWidthText, {
        text: link.label,
        toolTipText: link.toolTip
      })
    }), renderNestedLinkArrow(link, isNested)), renderNestedList(link, isNested));
  };
  return /*#__PURE__*/React.createElement(List, {
    className: "sovosNavigationLinks"
  }, links.map(link => renderLinkItem(link)));
};
NavigationLinks.propTypes = navigationLinksPropTypes;
export default NavigationLinks;